import { useMemo, useState } from "react";
import { Chip, Toast } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { marketingTerms, type TermsVersionSummary } from "@/api/marketingTerms.service";
import { TermsEditorPanel } from "./TermsEditorPanel";
import styles from "./Terms.module.css";

/** Every cell gets the row anyway; the value argument is only there to be skipped. */
type CellValue = TermsVersionSummary[keyof TermsVersionSummary];

function dateLabel(value: string): string {
  return new Date(value).toLocaleDateString();
}

function TermsPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.marketing.read");
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const { data, loading, error, refetch } = useQuery(() => marketingTerms.get(), []);

  const current = data?.current ?? null;

  const rows = useMemo<TermsVersionSummary[]>(
    () => [...(data?.versions ?? [])].sort((a, b) => b.version - a.version),
    [data],
  );

  const columns = useMemo<Column<TermsVersionSummary>[]>(
    () => [
      {
        header: "Version",
        accessor: "version",
        render: (_value: CellValue, row: TermsVersionSummary) => (
          <span className={styles.versionCell}>
            <span>v{row.version}</span>
            {current?.version === row.version && (
              <Chip size="small" variant="filled" color="success">
                Current
              </Chip>
            )}
          </span>
        ),
      },
      {
        header: "Published",
        accessor: "publishedAt",
        render: (_value: CellValue, row: TermsVersionSummary) => dateLabel(row.publishedAt),
      },
      {
        header: "Published by",
        accessor: "publishedBy",
        render: (_value: CellValue, row: TermsVersionSummary) => row.publishedBy ?? "—",
      },
      {
        header: "Last corrected",
        accessor: "updatedAt",
        render: (_value: CellValue, row: TermsVersionSummary) =>
          row.updatedAt === row.publishedAt ? "—" : dateLabel(row.updatedAt),
      },
      {
        header: "Acceptances",
        accessor: "acceptanceCount",
        render: (_value: CellValue, row: TermsVersionSummary) => String(row.acceptanceCount),
      },
    ],
    [current?.version],
  );

  const errorStatus = (error as (Error & { status?: number }) | null)?.status;

  if (!canRead || errorStatus === 401 || errorStatus === 403) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Marketing Toolkit Terms"
        subtitle="The terms and conditions a firm accepts before it downloads"
      />

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading ? (
        <LoadingState message="Loading the terms…" />
      ) : (
        <>
          {current === null ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>No terms have been published yet.</p>
              <p className={styles.emptyNote}>
                Write the terms below and publish them as version 1. Until then, nobody at a firm is
                asked to accept anything.
              </p>
            </div>
          ) : (
            <div className={styles.tableSection}>
              <DataTable
                data={rows}
                columns={columns}
                rowKey="id"
                emptyMessage="No versions published yet."
              />
            </div>
          )}

          <TermsEditorPanel
            current={current}
            onSaved={message => setToast({ message, type: "success" })}
            onError={message => setToast({ message, type: "error" })}
            refetchTerms={refetch}
          />
        </>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default TermsPage;
